import React, { useEffect, useState } from "react";
import { Button, Input, Col, Form, message, Switch, Row } from "antd";
import { useUpdateGeneralSetting, useFetchGeneralSetting, useUpdateGeneralSettingStatus } from "../services/generalsetting.service";
import { IConutrygeneralSetting } from "../types/generalsetting";
import IResponse from "../types/response";
import { isNotNullUndefined, isNotNullUndefinedBlank } from "../helpers/common";
import { storage } from "../utils/storage";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import { Select } from "antd";
import { useFetchCountry } from "../services/countries.service";

const GeneralSetting: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [settingData, setSettingData] = useState<IConutrygeneralSetting[]>([]);
    const [countryID, setCountryID] = useState<string>('');
    const [countries, setCountries] = useState<any[]>([]);
    const [userRoleId, setUserRoleId] = useState<string>('');
    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

    useEffect(() => {
        loadPermissions();
    }, [permissionsLoaded]);

    const GeneralSettingEdit = hasPermission("SETTINGS", "UPDATE");

    useEffect(() => {
        let token = storage.getToken() ?? "";
        if (!isNotNullUndefinedBlank(token)) {
            navigate("/");
            return;
        }
        let countryId = storage.getCountryId() ?? "";
        setCountryID(countryId);
        let userRoleId = storage.getUserRoleId() ?? "";
        setUserRoleId(userRoleId);
        if (countryID != "") {
            fetchGeneralSetting();
            fetchCountryMutation.mutate({
                searchFilter: "",
                isServiceableAttendant: "",
                isServiceableTraveler: ""
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryID]);

    const fetchGeneralSetting = async () => {
        fetchGeneralSettingMutation.mutate({ settingCode: "", countryId: countryID });
    };

    const fetchGeneralSettingMutation = useFetchGeneralSetting({
        onSuccess: (response: IResponse) => {
            if (response && response.data) {
                const data = Array.isArray(response.data) ? response.data : [response.data];
                setSettingData(data);
            } else {
                message.error("Unexpected response format");
            }
        },
        onError: (error: any) => {
            message.error(error.message);
        },
    });

    const fetchCountryMutation = useFetchCountry({
        onSuccess: (response: any) => {
            if (response) {
                setCountries(response);
                const storedCountryId = storage.getCountryId();
                const defaultCountry = response.find((x: any) => x.countryId.toString() === storedCountryId);

                if (defaultCountry) {
                    setSelectedCountry({
                        value: defaultCountry.countryId,
                        label: getCountryLabel(defaultCountry),
                    });
                }
            }
        },
        onError: (error: any) => {
            message.error(error.message);
        },
    });

    const updateGeneralSetting = useUpdateGeneralSetting({
        onSuccess: (response) => {
            if (isNotNullUndefined(response) && isNotNullUndefined(response.data) && response.statusCode === 200) {
                message.success(response.statusMessage);
            } else {
                message.success(response.statusMessage);
            }
        },
        onError: (error) => {
            message.error(error.message);
        },
    });

    const updateGeneralSettingStatus = useUpdateGeneralSettingStatus({
        onSuccess: (response) => {
            if (isNotNullUndefined(response) && isNotNullUndefined(response.data) && response.statusCode === 200) {
                message.success(response.statusMessage);
                fetchGeneralSetting();
            } else {
                message.success(response.statusMessage);
                fetchGeneralSetting();
            }
        },
        onError: (error) => {
            message.error(error.message);
        },
    });

    const handleInputChange = (index: number, newValue: string) => {
        const updatedData = [...settingData];
        updatedData[index] = {
            ...updatedData[index],
            settingValue: newValue,
        };
        setSettingData(updatedData);
    };

    const handleCountryChange = (value: any) => {
        setSelectedCountry(value);
        setCountryID(value.value);

        storage.setCountryId(value.value); // optional if you store it

        fetchGeneralSettingMutation.mutate({
            settingCode: "",
            countryId: value.value,
        });
    };

    const getCountryLabel = (country: any) => (
        <div className="flex items-center">
            <img
                src={country.imageUrl}
                className="w-5 mr-2"
                alt="Country Flag"
            />
            {country.countryName}
        </div>
    );

    const onFinish = (values: any) => {
        for (const item of settingData) {
            if (!item.settingValue || item.settingValue.trim() === "") {
                message.error(`${item.settingName} cannot be blank`);
                return;
            }
        }
        const updatedSettings = settingData.map((item) => ({
            settingId: item.settingId,
            settingName: item.settingName,
            settingValue: item.settingValue,
            countryID: item.countryID
        }));

        updateGeneralSetting.mutate(updatedSettings);
    };

    const handleSurChargeStatus = (checked: boolean, SettingId: string) => {
        const updateSettingStatus = {
            settingID: SettingId,
            isActiveStatus: checked,
            countryId: countryID
        }
        updateGeneralSettingStatus.mutate(updateSettingStatus);
    }

    return (
        <div>
            <div className="flex items-center gap-4 pb-5">
                <h2 className="text-2xl font-semibold text-black">
                    General Setting
                </h2>

                {userRoleId === "1" && (
                    <div className="flex items-center gap-3">
                        <Select
                            labelInValue
                            value={selectedCountry}
                            className="min-w-[220px]"
                            placeholder="Select Country"
                            optionFilterProp="label"
                            onChange={handleCountryChange}
                            options={countries.map((x: any) => ({
                                label: getCountryLabel(x),
                                value: x.countryId,
                            }))}
                        />
                    </div>
                )}
                {settingData[0] && (
                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-md">
                        <img
                            src={settingData[0]?.imageUrl}
                            alt={settingData[0]?.countryName}
                            className="w-5 h-5 rounded-full object-cover"
                        />

                        <span className="text-sm font-medium text-gray-700">
                            {settingData[0]?.countryName} Settings
                        </span>
                    </div>
                )}
            </div>
            <div className="setting-input">
                <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{}}>
                    {settingData.map((item, index) => (
                        <React.Fragment key={item.settingId}>
                            <Form.Item name={item.settingId} hidden initialValue={item.settingValue}>
                                <Input />
                            </Form.Item>
                            <div className="flex items-center gap-3">
                                <Form.Item label={item.settingName} className="w-full mt-[18px] mb-0 mr-2">
                                    <div className="flex items-center gap-3">
                                        <Col span={5} className="flex items-center gap-3">
                                            <Input
                                                className="h-10 w-full"
                                                value={item.settingValue}
                                                min={item.settingCode === "RESTRICT_HOURS" ? 1 : undefined}
                                                max={item.settingCode === "RESTRICT_HOURS" ? 12 : undefined}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    if (item.settingCode === "RESTRICT_HOURS") {
                                                        if (val === "") {
                                                            handleInputChange(index, "");
                                                            return;
                                                        }
                                                        const num = Number(val);
                                                        if (num < 1 || num > 12) return;
                                                    }
                                                    handleInputChange(index, val);
                                                }} />
                                            {GeneralSettingEdit && (
                                                <Button
                                                    htmlType="submit"
                                                    className="bg-black text-white h-10 hover:!border-black hover:!bg-transparent hover:!text-black hover:transition hover:duration-500 hover:ease-in-out">
                                                    Save
                                                </Button>)}
                                        </Col>
                                        {GeneralSettingEdit &&
                                            (item.settingCode === "SURCHARGE_ON_1_HOUR" ||
                                                item.settingCode === "12_HOUR_DISCOUNT") && (
                                                <Switch
                                                    size="default"
                                                    className="ml-2"
                                                    checked={item.statusId === "1"}
                                                    onChange={(checked) =>
                                                        handleSurChargeStatus(checked, item.settingId)
                                                    }
                                                />
                                            )}
                                    </div>
                                </Form.Item>
                            </div>
                        </React.Fragment>
                    ))}
                </Form>
            </div>
        </div>
    );
};

export default GeneralSetting;